<template>
  <base-material-card id="app-step-form">
    <v-data-table
      class="sortable"
      :headers="headers"
      :items="displayableItems"
      disable-sort
      hide-default-footer
      disable-pagination
    >
      <template v-slot:footer>
        <v-btn v-if="!readOnly" class="mt-5" name="addStep" @click="addStep">Add Step</v-btn>
      </template>

      <template v-slot:item.appOrder="{ item }">
        <span class="handle">
          <v-icon v-if="!readOnly">mdi-drag</v-icon>
        </span>
      </template>

      <template v-slot:item.dispenseType="{ item }">
        <v-select
          :disabled="readOnly"
          :items="dispenseTypes"
          v-model="item.dispenseType"
          name="type"
        />
      </template>

      <template v-slot:item.chemical="{ item }">
        <v-select
          :items="chemicals"
          v-model="item.chemical"
          name="chemical"
          item-text="name"
          item-value="id"
          return-object
          :disabled="readOnly"
        />
      </template>

      <template v-slot:item.volume="{ item }">
        <WeightStep
          v-if="isWeightStep(item)"
          v-bind:gallons.sync="item.gallons"
          v-bind:ounces.sync="item.ounces"
          v-bind:weight.sync="item.weight"
          :ppg="getPPG(item)"
          :disabled="readOnly"
        />
        <FixedTimeStep
          v-else-if="isFixedTimeStep(item)"
          v-bind:minutes.sync="item.minutes"
          v-bind:seconds.sync="item.seconds"
          v-bind:forcedVolume.sync="item.forcedVolume"
          :disabled="readOnly"
        />
      </template>

      <template v-slot:item.action="{ item }">
        <v-icon v-if="!readOnly" small @click="deleteItem(item)">
          mdi-delete
        </v-icon>
      </template>
    </v-data-table>
  </base-material-card>
</template>
<script>
import Sortable from 'sortablejs';
import _ from 'lodash';

export default {
  components: {
    WeightStep: () => import('@/components/forms/ApplicationSteps/WeightStep.vue'),
    FixedTimeStep: () => import('@/components/forms/ApplicationSteps/FixedTimeStep.vue'),
  },
  props: {
    items: {
      type: Array,
      default() {
        return [];
      },
    },
    chemicals: {
      type: Array,
      default() {
        return [];
      },
    },
    readOnly: {
      type: Boolean,
      default() {
        return true;
      },
    },
  },
  computed: {
    displayableItems() {
      return this.items.filter((item) => {
        return !item.toDelete;
      });
    },
  },
  data() {
    return {
      headers: [
        { text: 'Order', value: 'appOrder' },
        { text: 'Type', value: 'dispenseType' },
        { text: 'Chemical', value: 'chemical' },
        { text: '', value: 'volume' },
        { text: '', value: 'action' },
      ],

      dispenseTypes: [
        {
          value: 0,
          text: 'Weight',
          form: 'WeightStep',
        },
        {
          value: 1,
          text: 'Fixed Time',
          form: 'FixedTime',
        },
        {
          value: 2,
          text: 'Calibrated Time',
          form: 'WeightStep',
        },
        {
          value: 5,
          text: 'Parallel Fixed Time',
          form: 'FixedTime',
        },
        {
          value: 6,
          text: 'Parallel Calibrated Time',
          form: 'WeightStep',
        },
      ],
    };
  },
  methods: {
    isWeightStep(item) {
      return _.find(this.dispenseTypes, { value: item.dispenseType }).form === 'WeightStep';
    },
    isFixedTimeStep(item) {
      return _.find(this.dispenseTypes, { value: item.dispenseType }).form === 'FixedTime';
    },
    getPPG(item) {
      if (item.chemical && item.chemical.ppg) {
        return item.chemical.ppg;
      } else {
        const water = _.find(this.chemicals, { id: '-2' });
        return water.ppg;
      }
    },
    addStep() {
      const duplicate = _.clone(this.items);
      let appOrder = 0;
      if (!_.isEmpty(this.items)) {
        appOrder = _.last(duplicate).appOrder + 1;
      }
      duplicate.push({ appOrder, dispenseType: 0 });
      this.$emit('update:items', duplicate);
    },
    deleteItem(item) {
      if (!window.confirm('Are you sure you want to delete this item?')) {
        return;
      }
      const duplicate = _.clone(this.items);
      const step = _.find(duplicate, { appOrder: item.appOrder });
      if (step.id) {
        step.toDelete = true;
      } else {
        _.remove(duplicate, { appOrder: item.appOrder });
      }
      this.applySort(duplicate);
      this.$emit('update:items', duplicate);
    },
    applySort(items) {
      items.forEach((item, index) => {
        item.appOrder = index;
      });
    },
    dragEnd({ item, newIndex, oldIndex }) {
      item.className = 'just-dragged';
      setTimeout(() => {
        item.className = 'was-dragged';
      }, 600);

      const duplicate = _.clone(this.items);
      const rowSelected = duplicate.splice(oldIndex, 1)[0];
      rowSelected.justDragged = true;
      duplicate.splice(newIndex, 0, rowSelected);
      this.applySort(duplicate);
      this.$emit('update:items', duplicate);
    },
  },
  mounted() {
    const sortedItems = _.sortBy(this.items, 'appOrder');
    const duplicate = _.clone(sortedItems);
    this.applySort(duplicate);
    this.$emit('update:items', duplicate);

    const table = document.querySelector('.v-data-table.sortable tbody');
    Sortable.create(table, {
      enabled: !this.readOnly,
      handle: '.handle',
      onEnd: this.dragEnd,
      animation: 300,
    });
  },
};
</script>

<style lang="scss">
#app-step-form {
  .handle {
    cursor: move !important;
    cursor: -webkit-grabbing !important;
  }
  .v-data-table-header-mobile {
    display: none;
  }
  .v-data-table__mobile-row {
    margin-bottom: 4px;
  }

  .v-data-table__mobile-row:first-child {
    margin-top: 16px;
  }

  .v-data-table {
    td {
      height: inherit;
      min-height: 40px;
    }

    tbody tr:last-child td:not(.v-data-table__mobile-row) {
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    }

    tbody tr:hover:not(.v-data-table__expanded__content) {
      background: inherit;
    }
  }
}

.sortable-drag {
  box-shadow: 0 0 20px #888;
  background: white;
}

.sortable-ghost {
  opacity: 0.1;
}

tr.just-dragged {
  transition: all 0.3s ease;
  background: rgba(0, 174, 239, 0.5);
}
tr.was-dragged {
  transition: all 0.3s ease;
  background: white;
}
</style>
